import { useState, useEffect, useCallback } from "react";
import PageTitle from "../../../components/ui/PageTitle";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import UserInfoDisplay from "../../../components/user/UserInfoDisplay";
import Button from "../../../components/ui/Button";
import {
  getUserProfile,
} from "../../../lib/apiClient";
import {
  UserProfile,
} from "../../../types";
import { getErrorMessage } from "../../../lib/utils";
import toast from "react-hot-toast";

export default function UserProfilePage() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getUserProfile();
      setProfile(data);
    } catch (err) {
      const message = getErrorMessage(err);
      console.error("Failed to fetch profile:", message);
      setError(message);
      toast.error(`Error loading profile: ${message}`, {
        id: "fetch-profile-error",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, []);

  const avatarLetter = (profile?.name || profile?.role || "A")
    .charAt(0)
    .toUpperCase();

  if (isLoading) {
    return (
      <div className="space-y-6 md:space-y-8">
        <PageTitle title="My Profile" />
        <div className="flex justify-center items-center py-10">
          <LoadingIndicator size="lg" text="Loading your profile..." />{" "}
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="space-y-6 md:space-y-8">
        <PageTitle title="My Profile" />
        <div className="py-10">
          <ErrorMessage
            title="Error Loading Profile"
            message={error || "Could not load profile data."}
          />
        </div>
        <div className="flex justify-center">
          <Button onClick={fetchProfile}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-primary to-dark-secondary/30">
      <div className="w-full px-5 sm:px-7 md:px-9 lg:px-10 py-4 sm:py-6 space-y-6 sm:space-y-7">
  <div className="flex items-center gap-4">
    <div className="w-12 h-12 rounded-full bg-golden-accent text-black font-bold text-xl flex items-center justify-center shadow-lg flex-shrink-0">
      {avatarLetter}
    </div>
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold text-golden-accent leading-tight">
        Welcome back, {profile.name || "Admin"}
      </h1>
      <p className="text-text-secondary text-sm sm:text-base leading-tight mt-0.5">
        Manage your admin account details and linked services
      </p>
    </div>
  </div>

        <div className="grid grid-cols-1 gap-6 sm:gap-7">
          <div>
            <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-dark-border/50 shadow-xl sm:shadow-2xl hover:shadow-golden-accent/10 transition-all duration-300">
              <UserInfoDisplay profile={profile} isadmin={true} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
